import React, { useState } from 'react';
import type { EventLogEntry } from '@mariana/labos-client';
import { formatDate } from '../utils/formatDate';
import { EventsIcon } from './icons';

interface EventsTableProps {
  events: EventLogEntry[];
  showEntityColumns?: boolean;
  compact?: boolean;
  onEntityClick?: (entityType: string, entityId: number) => void;
}

const COMPACT_ATTRS = 2;
const COMPACT_LISTENERS = 1;

function TruncatedAttrs({ attrs, compact }: { attrs: string[]; compact: boolean }) {
  const [expanded, setExpanded] = useState(false);
  const limit = compact ? COMPACT_ATTRS : 5;
  const visible = expanded ? attrs : attrs.slice(0, limit);
  const overflow = attrs.length - limit;

  return (
    <div className={`changed-attrs${compact && !expanded ? ' compact' : ''}`}>
      {visible.map((attr) => (
        <span key={attr} className="changed-attr-tag">{attr}</span>
      ))}
      {!expanded && overflow > 0 && (
        <button className="overflow-toggle" onClick={(e) => { e.stopPropagation(); setExpanded(true); }}>
          +{overflow} more
        </button>
      )}
      {expanded && attrs.length > limit && (
        <button className="overflow-toggle" onClick={(e) => { e.stopPropagation(); setExpanded(false); }}>
          less
        </button>
      )}
    </div>
  );
}

function TruncatedListeners({ listeners, compact }: { listeners: EventLogEntry['listeners']; compact: boolean }) {
  const [expanded, setExpanded] = useState(false);
  if (!listeners || listeners.length === 0) {
    return <span style={{ color: 'var(--text-muted)', fontSize: '0.8125rem' }}>none</span>;
  }

  const limit = compact ? COMPACT_LISTENERS : listeners.length;
  const visible = expanded ? listeners : listeners.slice(0, limit);
  const overflow = listeners.length - limit;

  return (
    <div className={`listener-badges${compact && !expanded ? ' compact' : ''}`}>
      {visible.map((listener) => (
        <span
          key={listener.name}
          className={`listener-badge ${listener.status}`}
        >
          <span className="listener-badge-icon">
            {listener.status === 'processed' ? '✓' : listener.status === 'skipped' ? '–' : '✗'}
          </span>
          {listener.name}
          <span className="listener-tooltip">
            {listener.name}: {listener.status} ({listener.durationMs}ms)
            {listener.result?.message && ` — ${listener.result.message}`}
            {listener.result && !listener.result.message && (
              <pre style={{ margin: '4px 0 0', fontSize: '0.75rem' }}>{JSON.stringify(listener.result, null, 2)}</pre>
            )}
          </span>
        </span>
      ))}
      {!expanded && overflow > 0 && (
        <button className="overflow-toggle" onClick={(e) => { e.stopPropagation(); setExpanded(true); }}>
          +{overflow} more
        </button>
      )}
      {expanded && listeners.length > limit && (
        <button className="overflow-toggle" onClick={(e) => { e.stopPropagation(); setExpanded(false); }}>
          less
        </button>
      )}
    </div>
  );
}

export const EventsTable: React.FC<EventsTableProps> = ({
  events,
  showEntityColumns = true,
  compact = false,
  onEntityClick,
}) => {
  if (events.length === 0) {
    return (
      <div className="events-empty-state">
        <span className="empty-state-icon"><EventsIcon /></span>
        <p>No events recorded yet</p>
      </div>
    );
  }

  return (
    <table className={showEntityColumns ? 'events-table' : 'entity-events-table'}>
      <thead>
        <tr>
          <th>ID</th>
          <th>Timestamp</th>
          <th>Action</th>
          {showEntityColumns && <th>Entity Type</th>}
          {showEntityColumns && <th>Entity ID</th>}
          <th>Changed Attributes</th>
          <th>Listeners</th>
        </tr>
      </thead>
      <tbody>
        {events.map((event) => (
          <tr key={event.id}>
            <td>{event.id}</td>
            <td>{formatDate(event.createdAt)}</td>
            <td>
              <span className={`action-badge ${event.action}`}>
                {event.action}
              </span>
            </td>
            {showEntityColumns && (
              <td><span className="entity-type-label">{event.entityType}</span></td>
            )}
            {showEntityColumns && (
              <td>
                <span
                  className="events-entity-link"
                  onClick={() => onEntityClick?.(event.entityType, event.entityId)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      onEntityClick?.(event.entityType, event.entityId);
                    }
                  }}
                >
                  {event.entityId}
                </span>
              </td>
            )}
            <td>
              {event.changedAttributes && event.changedAttributes.length > 0 ? (
                <TruncatedAttrs attrs={event.changedAttributes} compact={compact} />
              ) : (
                <span style={{ color: 'var(--text-muted)' }}>-</span>
              )}
            </td>
            <td>
              <TruncatedListeners listeners={event.listeners} compact={compact} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};
